import {
  Autocomplete,
  AutocompleteChangeReason,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  TextField,
} from "@mui/material";
import { Vocabulary } from "../../Utils/Vocabulary";
import { useContext, useEffect, useRef } from "react";
import { CRUDContext } from "./WorkPointsTable";
import { endpoints } from "../../Utils/UrlEnum";
import { Controller, useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { postData } from "../../Services/postData";
import { updateData } from "../../Services/updateData";
import { WorkPointFormProps } from "../../Utils/Types";
import { FormsIds } from "../../Utils/Constants";
import Config from "../../Utils/Config";
import { WorkPointFormValidationSchema } from "../../Utils/Models";
import { getValueForAutocomplete, onFormError } from "../../Utils/Utils";

export default function WorkPointsForm(props: WorkPointFormProps) {
  const workPointContext: any = useContext(
    props.selectedWorkPoint ? props.selectedWorkPoint : CRUDContext
  );
  const companies = props.companies || [];
  const ref: any = useRef();
  const firstUpdate = useRef(true);
  const {
    register,
    control,
    handleSubmit,
    watch,
    getValues,
    formState,
    reset,
  } = useForm({
    resolver: yupResolver(WorkPointFormValidationSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    context: undefined,
    shouldFocusError: true,
    shouldUnregister: false,
    criteriaMode: "firstError",
    defaultValues: {
      visible: true,
      isMain: false,
    },
  });
  const formData = useWatch({ control: control });

  /**
   *
   */
  useEffect(() => {
    if (formData && !firstUpdate.current) {
      props.setContextValue({
        ...formData,
      });
    }
  }, [formData]);

  /**
   *
   */
  useEffect(() => {
    if (workPointContext.name && workPointContext.name !== "")
      reset(workPointContext);
  }, []);

  /**
   *
   */
  useEffect(
    () => {
      if (firstUpdate.current) {
        firstUpdate.current = false;
        return;
      }
      if (!firstUpdate.current) ref.current.submit();
    },
    [
      // props.shouldHandleSubmit
    ]
  );

  /**
   *
   * @param id
   */
  const updateElement = async (model: any) => {
    const url = `${endpoints.workPoints.main}/${workPointContext?._id}`;
    const res = (await updateData(url, model)) as any;
    if (!res || res?.error) {
      return;
    }
    //   if (props.selectedWorkPoint) {
    //     localStorage.setItem("userName", res?.data?.username);
    //   }
    props.setContextValue([]);
    props.onClose();
  };

  /**
   *
   * @param id
   */
  const createElement = async (model: any) => {
    const url = `${endpoints.workPoints.main}`;
    const res = (await postData(url, model)) as any;
    if (!res || res?.error) {
      return;
    }
    props.onClose();
    props.setContextValue([]);
  };

  /**
   *
   * @param model
   */
  const onSubmit = (model: any) => {
    const payload = {
      ...model,
      companyId: model.companyId || null,
    };
    workPointContext._id ? updateElement(payload) : createElement(payload);
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit, onFormError)}
      method="get"
      ref={ref}
      id={FormsIds.workPointForm}
    >
      <Grid container spacing={Config.standardGridSpacing}>
        <Grid item xs={12} sm={12} md={6}>
          <TextField
            {...register("name")}
            id="name"
            name="name"
            label={Vocabulary.name}
            value={getValues("name") || ""}
            fullWidth
            variant="outlined"
            error={typeof formState.errors.name === "object"}
            helperText={formState.errors.name?.message?.toString()}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <TextField
            {...register("phoneNumber")}
            value={getValues("phoneNumber") || ""}
            name="phoneNumber"
            id="phoneNumber"
            label={Vocabulary.phoneNumber}
            fullWidth
            variant="outlined"
            error={typeof formState.errors.phoneNumber === "object"}
            helperText={formState.errors.phoneNumber?.message?.toString()}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <TextField
            {...register("email")}
            variant="outlined"
            name="email"
            value={getValues("email") || ""}
            id="email"
            label={Vocabulary.email}
            fullWidth
            error={typeof formState.errors.email === "object"}
            helperText={formState.errors.email?.message?.toString()}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <Controller
            name="companyId"
            control={control}
            defaultValue={null}
            render={({ field: { ref: fieldRef, ...field } }) => (
              <Autocomplete
                id="companyId"
                fullWidth
                freeSolo={false}
                disablePortal
                noOptionsText={Vocabulary.noResultsFound}
                options={companies}
                isOptionEqualToValue={(option: any, value: any) => {
                  if (typeof value === "string") return option._id === value;
                  return option._id === value?._id;
                }}
                getOptionLabel={(option: any) => option.name || ""}
                value={getValueForAutocomplete(companies, field.value)}
                onChange={(
                  _e: any,
                  _value: any,
                  reason: AutocompleteChangeReason,
                  details?: any
                ) => {
                  if (reason === "clear") {
                    field.onChange(null);
                  } else {
                    field.onChange(details?.option?._id || null);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={Vocabulary.company}
                    variant="outlined"
                    fullWidth
                  />
                )}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <FormControlLabel
            control={
              <Checkbox
                {...register("visible")}
                checked={watch(`visible`)}
                // value={getValues("visible") || true}
                name="visible"
                id="visible"
              />
            }
            label={Vocabulary.isActive}
          />
          <FormControlLabel
            control={
              <Checkbox
                {...register("isMain")}
                checked={watch(`isMain`)}
                name="isMain"
                id="isMain"
              />
            }
            label={Vocabulary.isMain}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <TextField
            {...register("address.country")}
            value={getValues("address.country") || ""}
            name="address.country"
            id="address.country"
            label={Vocabulary.country}
            fullWidth
            variant="outlined"
          />
        </Grid>

        <Grid item xs={12} sm={12} md={6}>
          <TextField
            {...register("address.city")}
            value={getValues("address.city") || ""}
            name="address.city"
            id="address.city"
            label={Vocabulary.city}
            fullWidth
            variant="outlined"
          />
        </Grid>

        <Grid item xs={12} sm={12} md={6}>
          <TextField
            {...register("address.county")}
            value={getValues("address.county") || ""}
            name="address.county"
            id="address.county"
            label={Vocabulary.county}
            fullWidth
            variant="outlined"
          />
        </Grid>

        <Grid item xs={12} sm={12} md={6}>
          <TextField
            {...register("address.postcode")}
            value={getValues("address.postcode") || ""}
            name="address.postcode"
            id="address.postcode"
            label={Vocabulary.postcode}
            fullWidth
            variant="outlined"
          />
        </Grid>

        <Divider sx={{ width: "100%", padding: "10px 0px" }} />
        <Grid item xs={12} sm={12} md={6}>
          <TextField
            {...register("automaticCategoryPriceIncrease")}
            value={getValues("automaticCategoryPriceIncrease") || ""}
            name="automaticCategoryPriceIncrease"
            id="automaticCategoryPriceIncrease"
            label={Vocabulary.automaticCategoryPriceIncrease}
            fullWidth
            variant="outlined"
            type="number"
            InputProps={{ endAdornment: Vocabulary.lei }}
          />
        </Grid>

        <Divider sx={{ width: "100%", padding: "10px 0px" }} />
        <Grid item xs={12}>
          <h3>{Vocabulary.taxData}</h3>
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <TextField
            {...register("schoolName")}
            variant="outlined"
            name="schoolName"
            value={getValues("schoolName") || ""}
            id="schoolName"
            label={Vocabulary.schoolName}
            fullWidth
            error={typeof formState.errors.schoolName === "object"}
            helperText={formState.errors.schoolName?.message?.toString()}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <TextField
            {...register("serialNumber")}
            variant="outlined"
            name="serialNumber"
            value={getValues("serialNumber") || ""}
            id="serialNumber"
            label={Vocabulary.serialNumber}
            fullWidth
            error={typeof formState.errors.serialNumber === "object"}
            helperText={formState.errors.serialNumber?.message?.toString()}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <TextField
            {...register("number")}
            variant="outlined"
            name="number"
            value={getValues("number") || ""}
            id="number"
            label={Vocabulary.number}
            fullWidth
            error={typeof formState.errors.number === "object"}
            helperText={formState.errors.number?.message?.toString()}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <TextField
            {...register("administrator")}
            variant="outlined"
            name="administrator"
            value={getValues("administrator") || ""}
            id="administrator"
            label={Vocabulary.administrator}
            fullWidth
            error={typeof formState.errors.administrator === "object"}
            helperText={formState.errors.administrator?.message?.toString()}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <TextField
            {...register("director")}
            variant="outlined"
            name="director"
            value={getValues("director") || ""}
            id="director"
            label={Vocabulary.director}
            fullWidth
            error={typeof formState.errors.director === "object"}
            helperText={formState.errors.director?.message?.toString()}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <TextField
            {...register("oblioSerialNumber")}
            variant="outlined"
            name="oblioSerialNumber"
            value={getValues("oblioSerialNumber") || ""}
            id="oblioSerialNumber"
            label={`${Vocabulary.oblio} ${Vocabulary.serialNumber}`}
            fullWidth
            error={typeof formState.errors.oblioSerialNumber === "object"}
            helperText={formState.errors.oblioSerialNumber?.message?.toString()}
          />
        </Grid>
      </Grid>
    </form>
  );
}
